import { z } from 'zod'
// batch-65 factuur-richting (FO-486) - richting-/verplaatsingsvelden op de DTO's.
import type {
  BoekAlsInkoopBeschikbaarheidDTO,
  FactuurRichtingSamenvattingDTO,
} from './factuur-richting'
import type { InkoopFactuurStatus } from './inkoop'
// batch-66 verkoop-extractie (FO-493) - opmaakprofiel, zekerheid en koppeling.
import type {
  ExtractieProfielRefDTO,
  ExtractieWerkbonMarkeringDTO,
  VerkoopConceptExtractieDTO,
} from './factuur-extractie'
import type { FactuurVerzoekKoppelingDTO } from './facturen-bronregel'
import type { NummerreeksImportResultaat } from './facturen'

// ─── Verkoopfacturen-mailbox (postvak verkoopfactuur@streep.nl) ─────────────
// Buiten Portal v3 opgemaakte STREEP-verkoopfacturen komen per mail binnen,
// worden door Claude uitgelezen en als concept klaargezet in de controleer-
// wachtrij. Pas bij goedkeuren ontstaat een echte Invoice (origin=IMPORT).
// Gedeeld tussen API (routes/verkoop-mail.ts) en backoffice-UI (/facturen).

export const VERKOOP_MAIL_INGEST_STATUS = [
  'NIEUW',
  'GEEN_BIJLAGE',
  'VERWERKT',
  'FOUT',
  'DUPLICAAT',
  // Bijlage door de malware-scan geblokkeerd; alleen een ADMIN kan vrijgeven
  // via de quarantaine-tab in de mail-module (fase F, spec 44).
  'QUARANTAINE',
] as const
export type VerkoopMailIngestStatus = (typeof VERKOOP_MAIL_INGEST_STATUS)[number]

export const VERKOOP_MAIL_INGEST_STATUS_LABELS: Record<VerkoopMailIngestStatus, string> = {
  NIEUW: 'Binnengekomen',
  GEEN_BIJLAGE: 'Geen bruikbare bijlage',
  VERWERKT: 'Uitgelezen',
  FOUT: 'Mislukt',
  DUPLICAAT: 'Duplicaat',
  QUARANTAINE: 'Quarantaine (malware-scan)',
}

export const VERKOOP_MAIL_INGEST_STATUS_BADGE_VARIANT: Record<
  VerkoopMailIngestStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  NIEUW: 'info',
  GEEN_BIJLAGE: 'secondary',
  VERWERKT: 'success',
  FOUT: 'danger',
  DUPLICAAT: 'secondary',
  QUARANTAINE: 'danger',
}

export const VERKOOP_CONCEPT_STATUS = [
  'TE_CONTROLEREN',
  'GOEDGEKEURD',
  'AFGEWEZEN',
  // batch-65 factuur-richting (FO-486) - als concept-inkoopfactuur geboekt;
  // telt niet als afwijzing (zie inkoopFactuurId op het concept).
  'VERPLAATST_NAAR_INKOOP',
] as const
export type VerkoopFactuurConceptStatus = (typeof VERKOOP_CONCEPT_STATUS)[number]

export const VERKOOP_CONCEPT_STATUS_LABELS: Record<VerkoopFactuurConceptStatus, string> = {
  TE_CONTROLEREN: 'Te controleren',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
  VERPLAATST_NAAR_INKOOP: 'Geboekt als inkoop',
}

export const VERKOOP_CONCEPT_STATUS_BADGE_VARIANT: Record<
  VerkoopFactuurConceptStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  TE_CONTROLEREN: 'warning',
  GOEDGEKEURD: 'success',
  AFGEWEZEN: 'secondary',
  VERPLAATST_NAAR_INKOOP: 'info',
}

/** Match-confidence van de klant-/BV-suggestie (spiegelt import-handler). */
export type VerkoopMatchZekerheid = 'high' | 'medium' | 'low' | 'none'

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Eén rij in het postvak (/facturen?tab=postvak). */
export interface VerkoopMailIngestRowDTO {
  id: string
  mailbox: string
  afzender: string | null
  onderwerp: string | null
  ontvangenOp: string | null
  status: VerkoopMailIngestStatus
  aantalBijlagen: number
  foutmelding: string | null
  verwerktOp: string | null
  /** Concepten uit deze mail: id + status + (na goedkeuren) invoiceId. */
  concepten: {
    id: string
    status: VerkoopFactuurConceptStatus
    invoiceId: string | null
    // batch-65 factuur-richting (FO-486) - "Geboekt als inkoop: naar factuur".
    // Null zonder Inkoop-inzage (dan zonder doorklik; review K7).
    inkoopFactuurId: string | null
    /** Richting-oordeel HOOG inkoop (badge + rij-actie in het postvak). */
    vermoedelijkInkoop: boolean
    /** Voor de knopstand van de rij-actie (intern-pad, FO-432; review I4). */
    isIntercompany: boolean
    richting: FactuurRichtingSamenvattingDTO | null
    /** De geboekte Invoice is intussen verwijderd (FO-443): dan weer verplaatsbaar. */
    invoiceVerwijderd: boolean
  }[]
}

/** Eén rij in de controleer-wachtrij (/facturen?tab=controleren). */
export interface VerkoopConceptRowDTO {
  id: string
  status: VerkoopFactuurConceptStatus
  bronBestand: string
  factuurnummer: string | null
  factuurdatum: string | null
  totaal: string | null
  klant: { id: string; name: string } | null
  entity: { id: string; name: string } | null
  clientMatchZekerheid: VerkoopMatchZekerheid | null
  entityMatchZekerheid: VerkoopMatchZekerheid | null
  ontvangenOp: string | null
  afzender: string | null
  createdAt: string
  // FO-419 (batch-55) — herkend als factuur tussen eigen BV's (afzender én
  // ontvanger een STREEP-BV, incl. alias); de reviewer bevestigt bij goedkeuren.
  isIntercompany: boolean
  intercompanyTegenpartij: { id: string; name: string } | null
  // batch-65 factuur-richting (FO-486) - voorsorteren + herkomst.
  /** Richting-oordeel met HOGE zekerheid INKOOP: filter "Vermoedelijk inkoop". */
  vermoedelijkInkoop: boolean
  finqleKostenfactuur: boolean
  richting: FactuurRichtingSamenvattingDTO | null
  /**
   * Vanuit inkoop verplaatst ("Boeken als verkoopfactuur"). Id + nummer alleen
   * met Inkoop-inzage (review K7); anders null.
   */
  herkomstInkoopFactuur: { id: string | null; factuurNr: string | null } | null
  /** Na verplaatsen naar inkoop: de inkoopfactuur (doorklik; null zonder Inkoop-inzage). */
  inkoopFactuurId: string | null
  // batch-66 verkoop-extractie (FO-493) - opmaakprofiel + zekerheid in de wachtrij.
  /** Het opmaakprofiel waarmee het concept is uitgelezen (null = generiek). */
  profiel: ExtractieProfielRefDTO | null
  /** Aandeel velden met hoge zekerheid (0-100); null bij concepten van voor FO-493. */
  zekerheidScore: number | null
  /** Het concept wacht op "opnieuw uitlezen" (bulk). */
  wachtOpHerlezen: boolean
}

/** Volledig concept voor het review-scherm. */
export interface VerkoopConceptDetailDTO {
  id: string
  status: VerkoopFactuurConceptStatus
  bronBestand: string
  factuurnummer: string | null
  factuurdatum: string | null
  vervaldatum: string | null
  subtotaal: string | null
  btwBedrag: string | null
  totaal: string | null
  btwPercentage: string | null
  omschrijving: string | null
  referentienummer: string | null
  klant: { id: string; name: string } | null
  entity: { id: string; name: string } | null
  clientMatchZekerheid: VerkoopMatchZekerheid | null
  entityMatchZekerheid: VerkoopMatchZekerheid | null
  markeringen: string[]
  // FO-419 (batch-55) — intercompany-suggestie (beide BV's herkend); de reviewer
  // kan 'm in het review-scherm bevestigen, aanpassen of uitzetten.
  isIntercompany: boolean
  intercompanyTegenpartij: { id: string; name: string } | null
  invoiceId: string | null
  /** De geboekte Invoice is intussen verwijderd (FO-443): geen doorklik meer. */
  invoiceVerwijderd: boolean
  afwijsReden: string | null
  beoordeeldOp: string | null
  /** Ruwe namen uit de extractie — context wanneer de match ontbrak. */
  extractie: {
    debiteurnaam: string | null
    debiteurKvk: string | null
    bvNaam: string | null
  }
  /**
   * Factuurregels uit de extractie. Worden bij goedkeuren overgenomen als hun
   * som gelijk is aan het bevestigde bedrag excl. (1 cent tolerantie); anders
   * boekt de commit één samenvattende totaalregel. Read-only in de review-UI
   * zodat de reviewer ziet wat er op de factuur landt.
   */
  regels: {
    omschrijving: string
    aantal: number | null
    prijsPerStuk: number | null
    totaal: number | null
  }[]
  mail: {
    afzender: string | null
    onderwerp: string | null
    ontvangenOp: string | null
  }
  /** Ingestelde standaardstatus voor het boeken (instelbaar, zie instelling-DTO). */
  standaardStatus: VerkoopImportStandaardStatus
  // ─── batch-65 factuur-richting (FO-486) ─────────────────────────────────────
  vermoedelijkInkoop: boolean
  finqleKostenfactuur: boolean
  richting: FactuurRichtingSamenvattingDTO | null
  /** Kan "Boeken als inkoopfactuur" (voor deze gebruiker), en zo niet: waarom. */
  boekAlsInkoop: BoekAlsInkoopBeschikbaarheidDTO
  /** VERPLAATST_NAAR_INKOOP: waar het concept heen ging, door wie en wanneer. */
  verplaatsing: {
    /** De inkoopfactuur (doorklik); null zonder Inkoop-inzage (review K7). */
    inkoopFactuur: { id: string; factuurNr: string; status: InkoopFactuurStatus } | null
    /** Naam van de actor (null bij automatisch of een onbekende gebruiker). */
    verplaatstDoor: string | null
    verplaatstOp: string | null
    /** Automatisch verplaatst (instelling aan) - expliciet, niet afgeleid uit een lege naam. */
    automatisch: boolean
  } | null
  /** Vanuit inkoop verplaatst: de inkoopfactuur van herkomst + door wie/wanneer. */
  herkomstInkoopFactuur: {
    /** Id + nummer alleen met Inkoop-inzage (review K7); anders null. */
    id: string | null
    factuurNr: string | null
    verplaatstDoor: string | null
    verplaatstOp: string | null
  } | null
  // ─── batch-66 verkoop-extractie (FO-493) ────────────────────────────────────
  /**
   * De uitlezing: opmaakprofiel, zekerheid per veld, gestructureerde regels, de
   * koppeling met werkbonnen en of het concept opnieuw uit te lezen is.
   */
  uitlezing: VerkoopConceptExtractieDTO
}

/**
 * batch-66 verkoop-extractie (FO-493) - antwoord van POST
 * /verkoop-mail/concepten/:id/goedkeuren: de geboekte factuur, het effect op de
 * nummerreeks, de koppeling met het facturatieverzoek (FO-470), wat er met de
 * gekoppelde werkbonnen is gedaan (FO-449) en wat de leerlus heeft vastgelegd.
 */
export interface VerkoopConceptGoedkeurenResultaatDTO {
  invoiceId: string
  nummerreeks: NummerreeksImportResultaat | null
  koppeling: FactuurVerzoekKoppelingDTO | null
  werkbonnen: ExtractieWerkbonMarkeringDTO | null
  /** Aantal velden dat de reviewer anders invulde dan het systeem voorstelde. */
  correcties: number
  /** Voorstellen die door deze goedkeuring zijn ontstaan (ter bevestiging). */
  nieuweVoorstellen: number
}

// ─── Request-schemas (gedeeld form + API, zie CLAUDE.md Validatie) ───────────

export const verkoopMailIngestListQuerySchema = z.object({
  status: z.enum(VERKOOP_MAIL_INGEST_STATUS).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
})

export const verkoopConceptListQuerySchema = z.object({
  status: z.enum(VERKOOP_CONCEPT_STATUS).default('TE_CONTROLEREN'),
  // batch-65 factuur-richting (FO-486) - filter "Vermoedelijk inkoop (n)".
  vermoedelijkInkoop: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
})

/**
 * batch-65 factuur-richting (FO-486) - de lijst-meta van de controleer-
 * wachtrij: naast `total` (van de gekozen set) altijd de server-side telling
 * van de vermoedelijke inkoopfacturen onder de open concepten (filterbadge).
 */
export interface VerkoopConceptListMeta {
  total: number
  page: number
  pageSize: number
  vermoedelijkInkoopTotaal: number
}

/** Query van GET /verkoop-mail/concepten/:id/volgende (zelfde filter als de lijst). */
export const verkoopConceptVolgendeQuerySchema = z.object({
  vermoedelijkInkoop: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
})

/** Statussen waarmee een goedgekeurde import in het facturen-overzicht landt. */
export const VERKOOP_IMPORT_STANDAARD_STATUSSEN = ['VERZONDEN', 'BETAALD'] as const
export type VerkoopImportStandaardStatus = (typeof VERKOOP_IMPORT_STANDAARD_STATUSSEN)[number]

export const VERKOOP_IMPORT_STANDAARD_STATUS_LABELS: Record<VerkoopImportStandaardStatus, string> =
  {
    VERZONDEN: 'Verzonden (openstaand)',
    BETAALD: 'Betaald',
  }

/**
 * Body van POST /verkoop-mail/concepten/:id/goedkeuren. De reviewer bevestigt
 * of corrigeert de AI-voorgevulde velden; datums als YYYY-MM-DD.
 */
/** Kolommen zijn Decimal(12,2); ruim binnen de grens blijven voorkomt een
 *  Postgres numeric-overflow (500) op een absurde invoer. */
export const VERKOOP_BEDRAG_MAX = 99_999_999.99

/**
 * Hoeveel uitgelezen factuurregels er bij goedkeuren als losse regels worden
 * overgenomen; daarboven krijgt de factuur een samenvattende regel. De regels
 * van de AI komen uit een onvertrouwde PDF en blijven begrensd op 100. Regels
 * die een opmaakprofiel deterministisch uit de PDF-tekst las (batch-66, FO-493)
 * mogen met meer: een weekfactuur voor een grote klant heeft al snel meer dan
 * honderd uren- en kilometerregels.
 */
export const VERKOOP_REGELS_MAX = 100
export const VERKOOP_REGELS_MAX_UIT_TEKST = 500

const bedragSchema = z
  .number({ error: 'Vul een bedrag in' })
  .finite('Vul een bedrag in')
  .min(-VERKOOP_BEDRAG_MAX, 'Bedrag is te groot')
  .max(VERKOOP_BEDRAG_MAX, 'Bedrag is te groot')

export const verkoopConceptGoedkeurenSchema = z.object({
  clientId: z.string().min(1, 'Kies een klant'),
  entityId: z.string().min(1, 'Kies een BV'),
  factuurnummer: z
    .string()
    .trim()
    .min(1, 'Factuurnummer is verplicht')
    .max(64)
    // Zelfde tekenset als de import-wizard; het nummer landt verbatim in
    // Invoice.invoiceNumber en in bestandsnamen.
    .regex(/^[A-Za-z0-9._/ -]+$/, 'Factuurnummer bevat ongeldige tekens'),
  factuurdatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (jjjj-mm-dd)'),
  vervaldatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (jjjj-mm-dd)')
    .nullable()
    .optional(),
  // Bedragen als number; negatief = creditnota (commitVerkoopImport zet isCredit).
  subtotaal: bedragSchema,
  btwBedrag: bedragSchema,
  btwPercentage: z
    .number({ error: 'Vul een percentage in' })
    .min(0, 'Percentage tussen 0 en 100')
    .max(100, 'Percentage tussen 0 en 100'),
  omschrijving: z.string().trim().max(2000).nullable().optional(),
  referentienummer: z.string().trim().max(255).nullable().optional(),
  status: z.enum(VERKOOP_IMPORT_STANDAARD_STATUSSEN),
  // FO-419 (batch-55) — intercompany bevestigen of uitzetten. Weglaten = de
  // suggestie van het concept volgen. Bij `true` is de ontvangende BV verplicht
  // (uit het concept of hier meegegeven) en moet die verschillen van `entityId`;
  // de API weigert anders met een inline veldfout.
  isIntercompany: z.boolean().optional(),
  intercompanyTegenpartijEntityId: z.string().min(1).nullable().optional(),
  // batch-66 (FO-493) — de uitlezing die de reviewer voor zich had
  // (`uitlezing.herleesTeller` uit het detail). Is het concept intussen opnieuw
  // uitgelezen (bulk), dan weigert de API met 409 `CONCEPT_OPNIEUW_UITGELEZEN`:
  // anders boekt goedkeuren regels en een koppeling die de reviewer niet zag.
  // Weglaten mag alleen zolang het concept nooit opnieuw is uitgelezen (teller 0);
  // daarna weigert de API ook zonder teller.
  herleesTeller: z.number().int().min(0).optional(),
  // batch-66 (FO-493) — de vingerafdruk van het koppelbesluit dat op het scherm
  // stond (`uitlezing.koppelBesluit.vingerafdruk`, of die van het laatste
  // voorbeeld na een wijziging). Goedkeuren voert alleen uit wat de reviewer zag:
  // wijkt het besluit af, dan 409 `KOPPELING_GEWIJZIGD` en wordt er niets
  // geboekt. Weglaten = er is geen besluit getoond: de factuur wordt geboekt
  // zonder automatische koppeling aan werkbonnen.
  koppelingVingerafdruk: z.string().min(1).max(128).optional(),
})
export type VerkoopConceptGoedkeurenInput = z.infer<typeof verkoopConceptGoedkeurenSchema>

/**
 * Body van POST /verkoop-mail/concepten/:id/koppeling: de velden van het
 * reviewformulier die bepalen hoe de factuur aan werkbonnen gekoppeld wordt.
 * Alles mag leeg zijn (een half ingevuld formulier): dan valt er niets te koppelen.
 */
export const verkoopConceptKoppelingVoorbeeldSchema = z.object({
  clientId: z.string().min(1).max(64).nullable(),
  entityId: z.string().min(1).max(64).nullable(),
  referentienummer: z.string().trim().max(255).nullable(),
  factuurdatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (jjjj-mm-dd)')
    .nullable(),
  subtotaal: bedragSchema.nullable(),
  btwBedrag: bedragSchema.nullable(),
})
export type VerkoopConceptKoppelingVoorbeeldInput = z.infer<typeof verkoopConceptKoppelingVoorbeeldSchema>

/** Body van POST /verkoop-mail/concepten/:id/afwijzen. */
export const verkoopConceptAfwijzenSchema = z.object({
  reden: z.string().trim().min(3, 'Reden is verplicht').max(1000),
})
export type VerkoopConceptAfwijzenInput = z.infer<typeof verkoopConceptAfwijzenSchema>

/** Instelling: standaardstatus waarmee een goedgekeurde import wordt geboekt. */
export const verkoopImportInstellingSchema = z.object({
  standaardStatus: z.enum(VERKOOP_IMPORT_STANDAARD_STATUSSEN),
})
export interface VerkoopImportInstellingDTO {
  standaardStatus: VerkoopImportStandaardStatus
}

/**
 * Antwoord van GET /verkoop-mail/concepten/:id/volgende (FO-416, "Goedkeuren
 * en volgende"): het eerstvolgende open concept ná :id in exact de wachtrij-
 * volgorde van /facturen?tab=controleren, of `null` als de stapel leeg is.
 */
export interface VerkoopConceptVolgendeDTO {
  id: string | null
}
